import React, { useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Typography, Switch, Button, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import AlertModal from "../../Requirement/components/common/AlerModel";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transition: "transform 300ms ease-in-out",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#de6b14",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 22, height: 22 },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    backgroundColor: "#ccc",
    opacity: 1,
  },
}));

const SellerFarmerNotification = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    marketingEmails: false,
  });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const handleModalClose = () =>
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));

  const handleToggle = (name) => (e) =>
    setNotifications((prev) => ({ ...prev, [name]: e.target.checked }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: "Notification settings updated successfully.",
        autoClose: false,
      });
    } catch (error) {
      console.error("Error saving notification settings:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: "Failed to save notification settings.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };
    checkUser();
  }, [navigate]);

  return (
    <div className="container mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-4">
        <div className="table-header">
          <div className="header-text">
            <div className="head-text">Notification Settings</div>
            <div className="head-subtext">
              Manage how you receive notifications
            </div>
          </div>
          <div className="head-btn">
            <div className="submit-btn-form">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
        <div className="row g-3 mt-2">
          <div className="col-md-6">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Email Notifications
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Receive notifications via email
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={notifications.emailNotifications}
                    onChange={handleToggle("emailNotifications")}
                  />
                }
                label=""
              />
            </div>
          </div>
          <div className="col-md-6">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  SMS Notifications
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Receive notifications via SMS
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={notifications.smsNotifications}
                    onChange={handleToggle("smsNotifications")}
                  />
                }
                label=""
              />
            </div>
          </div>
          <div className="col-md-6">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Push Notifications
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Receive push notifications in browser
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={notifications.pushNotifications}
                    onChange={handleToggle("pushNotifications")}
                  />
                }
                label=""
              />
            </div>
          </div>
          <div className="col-md-6">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Marketing Emails
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Receive marketing and promotional emails
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={notifications.marketingEmails}
                    onChange={handleToggle("marketingEmails")}
                  />
                }
                label=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerFarmerNotification;
